import { useRef, type TouchEvent, type MouseEvent, useEffect } from "react";
import { Box, LabelWrapper, Button } from "@cruk/cruk-react-components";

import { isBrowser } from "@fwa/src/utils/browserUtils";
import { DrawingCanvas } from "@fwa/src/components/DrawingCanvas";

import sharedStyles from "@fwa/src/components/styles.module.css";

export const DrawingCanvasControls = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  // we use refs so we don't cause re-renders loosing current state of doodle
  const colourRef = useRef<HTMLInputElement | null>(null);
  const lineWidthRef = useRef<HTMLInputElement | null>(null);
  const isPainting = useRef(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const startDraw = () => {
    if (!isBrowser) return;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    ctx.beginPath();
    isPainting.current = true;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx || !isPainting.current || !colourRef || !lineWidthRef)
      return;
    ctx.lineWidth = parseInt(lineWidthRef.current?.value || "0", 10);
    ctx.strokeStyle = colourRef.current?.value || "#000000";
    ctx.lineCap = "round";
    ctx.lineTo(
      e.clientX - canvas.offsetLeft,
      e.clientY - canvas.offsetTop + window.scrollY,
    );
    ctx.stroke();
  };

  const handleTouchMove = (e: TouchEvent) => {
    const eventX = e.nativeEvent.touches?.[0]?.clientX;
    const eventY = e.nativeEvent.touches?.[0]?.clientY;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const canvasWrapper = canvasWrapperRef.current;

    if (
      !canvas ||
      !ctx ||
      !canvasWrapper ||
      !isPainting.current ||
      !colourRef ||
      !lineWidthRef ||
      eventX === undefined ||
      eventY === undefined
    )
      return;

    ctx.lineWidth = parseInt(lineWidthRef.current?.value || "0", 10);
    ctx.strokeStyle = colourRef.current?.value || "#000000";
    ctx.lineCap = "round";
    ctx.lineTo(
      eventX - canvas.offsetLeft,
      eventY - canvas.offsetTop + window.scrollY,
    );
    ctx.stroke();
  };

  const endDraw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    isPainting.current = false;
    ctx.beginPath();
  };

  useEffect(() => {
    if (isBrowser) {
      const canvas = canvasRef.current;
      const canvasWrapper = canvasWrapperRef.current;
      if (canvas && canvasWrapper) {
        canvas.width = canvasWrapper?.clientWidth;
        canvas.height = canvasWrapper?.clientHeight;
      }
    }
  }, []);

  // Also set canvas size when window resizes to ensure proper sizing
  useEffect(() => {
    const handleResize = () => {
      if (isBrowser) {
        const canvas = canvasRef.current;
        const canvasWrapper = canvasWrapperRef.current;
        if (canvas && canvasWrapper) {
          canvas.width = canvasWrapper?.clientWidth;
          canvas.height = canvasWrapper?.clientHeight;
        }
      }
    };

    if (isBrowser) {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  return (
    <>
      <Box
        id="controls"
        backgroundColor="background-mid"
        padding="xs"
        marginBottom="s"
        data-component="drawing-canvas-controls"
      >
        <div className={sharedStyles["row"]}>
          <Box paddingRight="xs" marginBottom="none">
            <LabelWrapper label="Colour">
              <input
                ref={colourRef}
                type="color"
                name="colour"
                defaultValue="var(--clr-primary, #005ea5)"
              />
            </LabelWrapper>
          </Box>
          <Box paddingRight="xs" marginBottom="none">
            <LabelWrapper label="Width">
              <input
                ref={lineWidthRef}
                type="number"
                id="lineWidth"
                name="lineWidth"
                defaultValue="12"
                style={{ width: "4em", fontSize: "1em" }}
              />
            </LabelWrapper>
          </Box>
          <div
            className={sharedStyles["column"]}
            style={{
              flexDirection: "column-reverse",
            }}
          >
            <Box paddingRight="xs">
              <Button full onClick={clear} data-cta-type="clear-canvas">
                Clear
              </Button>
            </Box>
          </div>
        </div>
      </Box>
      <Box
        ref={canvasWrapperRef}
        style={{ width: "100%", height: "50vh", border: "solid 1px black" }}
      >
        <DrawingCanvas
          ref={canvasRef}
          handleMouseDown={startDraw}
          handleTouchStart={startDraw}
          handleMouseUp={endDraw}
          handleTouchEnd={endDraw}
          handleMouseMove={handleMouseMove}
          handleTouchMove={handleTouchMove}
        />
      </Box>
    </>
  );
};

export default DrawingCanvasControls;
